import React, { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import Select from 'components/partials/select/select';
import Button from 'components/partials/button/button';

import { SELECT_CUSTOM_STYLES } from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-elements-form.const';
import { useCriticalElementForm } from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/context/context';

import { criticalElementsCategories } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-configuration/goals-configuration';

import Icons from 'assets/icons';

interface FieldOption {
  value: string;
  label: string;
  nested?: FieldConfig;
}

interface FieldConfig {
  required?: boolean;
  associatedPropertyName: string;
  displayName: string;
  options?: FieldOption[];
  multiple?: boolean;
}

interface CriticalElementDetailsProps {
  detailIndex: number;
  detailData?: unknown;
  isDisabled: boolean;
  onRemove?: (index: number) => void;
}

const CriticalElementDetails = ({
  detailIndex,
  isDisabled,
  onRemove,
}: CriticalElementDetailsProps): React.JSX.Element => {
  const { control, setValue, clearErrors } = useCriticalElementForm();
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [renderKey, setRenderKey] = useState<number>(0);

  const watchedDetail = useWatch({
    control,
    name: `details.${detailIndex}`,
  }) as Record<string, unknown> | undefined;

  useEffect(() => {
    if (fields.length) return;

    const extendedCategories = criticalElementsCategories || [];

    const categoryOptions = extendedCategories
      .filter((cat) => cat.name !== 'Topline' && cat.name !== 'Combined')
      .map((cat) => ({ value: cat.name, label: cat.name }));

    setFields([
      {
        required: true,
        associatedPropertyName: 'category',
        displayName: 'Category',
        options: categoryOptions,
      },
    ]);
  }, []);

  useEffect(() => {
    if (!watchedDetail?.category) return;

    const extendedCategories = criticalElementsCategories || [];

    const categoryConfig = extendedCategories.find(
      (cat) => cat.name === watchedDetail.category
    ) as { name: string; details?: FieldConfig } | undefined;

    if (!categoryConfig?.details) {
      setFields((prev) => prev.slice(0, 1));

      return;
    }

    const categoryOptions = extendedCategories
      .filter((cat) => cat.name !== 'Topline' && cat.name !== 'Combined')
      .map((cat) => ({ value: cat.name, label: cat.name }));

    const newFields: FieldConfig[] = [
      {
        required: true,
        associatedPropertyName: 'category',
        displayName: 'Category',
        options: categoryOptions,
      },
    ];

    let currentConfig: FieldConfig | undefined = categoryConfig.details;
    const currentPath: string[] = [];

    while (currentConfig) {
      newFields.push({
        ...currentConfig,
        associatedPropertyName: currentConfig.associatedPropertyName,
      });

      currentPath.push(currentConfig.associatedPropertyName);

      const selectedValue: unknown =
        watchedDetail?.[currentConfig.associatedPropertyName];

      if (!selectedValue) break;

      const selectedOption: FieldOption | undefined =
        currentConfig.options?.find((opt) => opt.value === selectedValue);

      if (!selectedOption?.nested) break;

      currentConfig = selectedOption.nested;
    }

    setFields(newFields);
    setRenderKey((prev) => prev + 1);
  }, [watchedDetail]);

  const handleFieldChange = (
    fieldName: string,
    selectedOption: FieldOption | { value: string } | null | undefined
  ): void => {
    if (!selectedOption) return;

    const value = selectedOption.value;

    setValue(`details.${detailIndex}.${fieldName}`, value);

    const allFields = fields.map((f) => f.associatedPropertyName);
    const currentFieldIndex = allFields.indexOf(fieldName);

    if (currentFieldIndex >= 0) {
      allFields.slice(currentFieldIndex + 1).forEach((dependentField) => {
        setValue(`details.${detailIndex}.${dependentField}`, '');
      });
    }
  };

  useEffect(() => {
    if (!fields.length) return;

    fields.forEach((field) => {
      if (!field) return;

      const options = field.options || [];
      const fieldName = field.associatedPropertyName;
      const currentValue = watchedDetail?.[fieldName];

      if (options.length === 1 && !currentValue) {
        const singleOption = options[0].value;

        setValue(`details.${detailIndex}.${fieldName}`, singleOption);
        handleFieldChange(fieldName, { value: singleOption });
      }
    });
  }, [fields, watchedDetail]);

  const renderField = (
    field: FieldConfig,
    index: number
  ): React.JSX.Element => {
    const {
      associatedPropertyName,
      displayName,
      options = [],
      required,
      multiple,
    } = field;

    const singleOption = options.length === 1;

    return (
      <div
        key={`${associatedPropertyName}-${detailIndex}-${renderKey}-${index}`}
        className="shrink-0"
      >
        <Controller
          name={`details.${detailIndex}.${associatedPropertyName}`}
          control={control}
          rules={{ required }}
          defaultValue={multiple ? [] : ''}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const selectValue = multiple
              ? options.filter(
                  (o) => Array.isArray(value) && value.includes(o.value)
                )
              : options.find((o) =>
                  Array.isArray(value)
                    ? o.value === value[0]
                    : o.value === value
                );

            return (
              <div className="flex items-center gap-x-2">
                <Select
                  value={selectValue}
                  onChange={(
                    opt: FieldOption | FieldOption[] | null | undefined
                  ) => {
                    if (!opt) {
                      onChange('');

                      return;
                    }

                    handleFieldChange(
                      associatedPropertyName,
                      Array.isArray(opt) ? opt[0] : opt
                    );
                    onChange(
                      multiple && Array.isArray(opt)
                        ? opt.map((o) => o.value)
                        : (opt as FieldOption).value
                    );
                  }}
                  options={options}
                  disabled={isDisabled || singleOption}
                  selectWrapperClass="!min-w-[150px]"
                  labelInTop
                  label={displayName}
                  isMulti={multiple}
                  fieldError={
                    error && { ...error, message: 'This field is required' }
                  }
                  clearInputErr={() =>
                    clearErrors(
                      `details.${detailIndex}.${associatedPropertyName}`
                    )
                  }
                  isClearable={!required}
                  required={required}
                  {...SELECT_CUSTOM_STYLES}
                />
                {onRemove && !isDisabled && fields.length - 1 === index && (
                  <Button
                    onClick={() => onRemove(detailIndex)}
                    type="button"
                    variant="reserve-text"
                    className="!h-auto !p-0 mt-10"
                  >
                    <Icons.XinCircle className="text-navy-500 h-5 w-5" />
                  </Button>
                )}
              </div>
            );
          }}
        />
      </div>
    );
  };

  return (
    <div className="flex gap-x-3 flex-wrap">
      {fields.map((f, idx) => renderField(f, idx))}
    </div>
  );
};

export default CriticalElementDetails;
